import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import api, { apiErr } from "../lib/api";
import { useLang } from "../lib/i18n";
import { useAuth } from "../context/AuthContext";
import { PageHeader } from "../lib/ui";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { ArrowLeft, Save, Loader2, RefreshCw, Wand2, CheckCircle2, BadgeCheck, Lock, Stamp, Download, Send, RotateCcw, Clock, Printer } from "lucide-react";
import { usePdfPreview } from "../components/PdfPreview";
import { ReportDashboard } from "../components/ReportDashboard";
import { ReportSteps } from "../components/ReportSteps";
import { ValidationTrail } from "../components/ValidationTrail";
import { AreasReport } from "../components/AreasTable";
import { ListsReport } from "../components/StructuredLists";
import { cacheGet, cacheSet, getLocalReport, queueReport } from "../lib/offline";
import { WifiOff } from "lucide-react";

export default function GeneratedReportView() {
  const { id } = useParams();
  const isLocal = id.startsWith("local:");
  const [fwCats, setFwCats] = useState([]);
  useEffect(() => { cacheGet("framework:fr").then((c) => setFwCats(c?.value?.activity_categories || [])); }, []);
  const { t, lang } = useLang();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [r, setR] = useState(null);
  const [narr, setNarr] = useState({});
  const [busy, setBusy] = useState("");
  const pdf = usePdfPreview();
  const [sig, setSig] = useState("");
  const [staleAt, setStaleAt] = useState("");
  const offline = typeof navigator !== "undefined" && !navigator.onLine;

  const load = async () => {
    if (isLocal) {
      const rec = await getLocalReport(id);
      if (!rec) { toast.error(t("Ce rapport hors ligne a déjà été synchronisé.")); navigate("/generated"); return; }
      setR(rec.report);
      setNarr(rec.report.narratives || {});
      return;
    }
    try {
      const res = await api.get(`/harmonized/reports/${id}`, { params: { lang } });
      setR(res.data);
      setNarr(res.data.narratives || {});
      setSig(res.data.signed_by || user?.name || "");
      setStaleAt("");
      await cacheSet(`report:${id}:${lang}`, res.data);
    } catch (err) {
      if (!err.response) {
        const c = await cacheGet(`report:${id}:${lang}`);
        if (c?.value) {
          setR(c.value);
          setNarr(c.value.narratives || {});
          setSig(c.value.signed_by || user?.name || "");
          setStaleAt(c.at);
          return;
        }
        toast.error(t("Hors ligne et ce rapport n'a pas encore été téléchargé."));
        return;
      }
      toast.error(apiErr(err.response?.data?.detail));
    }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [id, lang]);

  const save = async () => {
    setBusy("save");
    try {
      if (isLocal) {
        await queueReport({ ...r, narratives: narr, edited: true });
        toast.success(t("Modifications conservées sur l'appareil."));
        return;
      }
      await api.put(`/harmonized/reports/${id}`, { narratives: narr });
      toast.success(t("Modifications enregistrées."));
    } catch (err) { toast.error(apiErr(err.response?.data?.detail)); }
    finally { setBusy(""); }
  };

  const regenerate = async () => {
    setBusy("regen");
    try {
      const body = r.ptype === "daily"
        ? { ptype: "daily", date: r.period.from, volet: r.volet }
        : { ptype: "weekly", year: Number(r.period_key.slice(0, 4)), week: Number(r.period_key.slice(6)), volet: r.volet };
      await api.post("/harmonized/generate", body);
      await load();
      toast.success(t("Indicateurs recalculés depuis les saisies."));
    } catch (err) { toast.error(apiErr(err.response?.data?.detail)); }
    finally { setBusy(""); }
  };

  const copilot = async (theme) => {
    setBusy(`cop-${theme}`);
    try {
      const res = await api.post(`/harmonized/reports/${id}/copilot`, { theme });
      setNarr((n) => ({ ...n, [theme]: res.data.text }));
      toast.success(t("Proposition du copilote insérée. Relisez et ajustez."));
    } catch (err) { toast.error(apiErr(err.response?.data?.detail)); }
    finally { setBusy(""); }
  };

  const submit = async () => {
    setBusy("submit");
    try {
      await api.put(`/harmonized/reports/${id}`, { narratives: narr });
      await api.post(`/harmonized/reports/${id}/submit`, {});
      await load();
      toast.success(t("Rapport soumis pour validation."));
    } catch (err) { toast.error(apiErr(err.response?.data?.detail)); }
    finally { setBusy(""); }
  };

  const validate = async () => {
    setBusy("validate");
    try {
      await api.post(`/harmonized/reports/${id}/validate`, { signature: sig });
      await load();
      toast.success(t("Rapport validé et signé."));
    } catch (err) { toast.error(apiErr(err.response?.data?.detail)); }
    finally { setBusy(""); }
  };

  const sendBack = async () => {
    const motif = window.prompt(t("Motif du renvoi à l'auteur :"), "");
    if (!motif) return;
    setBusy("return");
    try {
      await api.post(`/harmonized/reports/${id}/return`, { reason: motif });
      await load();
      toast.success(t("Rapport renvoyé à son auteur."));
    } catch (err) { toast.error(apiErr(err.response?.data?.detail)); }
    finally { setBusy(""); }
  };

  const countersign = async () => {
    setBusy("counter");
    try {
      await api.post(`/harmonized/reports/${id}/countersign`, { signature: sig });
      await load();
      toast.success(t("Rapport consolidé."));
    } catch (err) { toast.error(apiErr(err.response?.data?.detail)); }
    finally { setBusy(""); }
  };

  const downloadPdf = async () => {
    setBusy("pdf");
    const name = `${r.ptype === "weekly" ? "Hebdo" : "Quotidien"}_${r.period_key}.pdf`;
    const save = (blob) => {
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = name;
      a.click();
      URL.revokeObjectURL(url);
    };
    try {
      const res = await api.get(`/harmonized/reports/${id}/pdf`, { responseType: "blob", params: { lang } });
      await cacheSet(`pdf:${id}:${lang}`, res.data);
      save(res.data);
      toast.success(t("PDF téléchargé."));
    } catch (err) {
      const c = await cacheGet(`pdf:${id}:${lang}`);
      if (c?.value) { save(c.value); toast.message(t("PDF hors ligne (dernière version téléchargée).")); }
      else if (!err.response) toast.error(t("PDF indisponible hors ligne : ouvrez-le une fois connecté."));
      else toast.error(apiErr(err.response?.data?.detail));
    }
    finally { setBusy(""); }
  };


  const previewPdf = async () => {
    try {
      await pdf.show(async () => {
        try {
          const res = await api.get(`/harmonized/reports/${id}/pdf`, { responseType: "blob", params: { lang } });
          await cacheSet(`pdf:${id}:${lang}`, res.data);
          return res.data;
        } catch (err) {
          const c = await cacheGet(`pdf:${id}:${lang}`);
          if (c?.value) return c.value;
          throw err;
        }
      }, { title: `${r.period_label} · ${r.scope_label}`, filename: `${r.ptype === "weekly" ? "Hebdo" : "Quotidien"}_${r.period_key}.pdf` });
    } catch (err) {
      if (!err.response) toast.error(t("PDF indisponible hors ligne : ouvrez-le une fois connecté."));
      else toast.error(apiErr(err.response?.data?.detail));
    }
  };

  const canValidate = ["provincial_supervisor", "admin"].includes(user?.role);
  const isAdmin = user?.role === "admin";

  if (!r) return <div className="flex justify-center py-20"><Loader2 className="h-6 w-6 animate-spin text-[#F05A28]" /></div>;

  const rows = (r.indicators || []).filter((x) => x.current || x.previous);
  const groups = r.narrative_groups || [];

  return (
    <div data-testid="generated-view-page">
      <button onClick={() => navigate("/generated")} className="flex items-center gap-1.5 text-sm text-slate-500 hover:text-slate-800 mb-3" data-testid="gv-back">
        <ArrowLeft className="h-4 w-4" /> {t("Retour")}
      </button>
      <PageHeader eyebrow={r.ptype === "weekly" ? "Rapport hebdomadaire dérivé" : "Rapport quotidien dérivé"}
        title={r.period_label} subtitle={`${r.scope_label} · ${r.entries_count} ${t("saisie(s) agrégée(s)")}`} testid="gv-header">
        <Button variant="outline" className="rounded-md" onClick={previewPdf} disabled={!!busy || isLocal} data-testid="gv-preview">
          <Printer className="h-4 w-4 mr-1.5" />{t("Aperçu / Imprimer")}
        </Button>
        <Button variant="outline" className="rounded-md" onClick={downloadPdf} disabled={!!busy || isLocal} data-testid="gv-pdf">
          {busy === "pdf" ? <Loader2 className="h-4 w-4 mr-1.5 animate-spin" /> : <Download className="h-4 w-4 mr-1.5" />}{t("Télécharger PDF")}
        </Button>
        <Button variant="outline" className="rounded-md" onClick={regenerate} disabled={!!busy || offline || isLocal} data-testid="gv-regen">
          {busy === "regen" ? <Loader2 className="h-4 w-4 mr-1.5 animate-spin" /> : <RefreshCw className="h-4 w-4 mr-1.5" />}{t("Régénérer")}
        </Button>
        <Button className="rounded-md bg-[#0F172A] hover:bg-[#1E293B]" onClick={save} disabled={!!busy || (offline && !isLocal)} data-testid="gv-save">
          {busy === "save" ? <Loader2 className="h-4 w-4 mr-1.5 animate-spin" /> : <Save className="h-4 w-4 mr-1.5" />}{t("Enregistrer")}
        </Button>
      </PageHeader>

      {isLocal && (
        <div className="mb-4 flex flex-wrap items-center gap-2 rounded-lg border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-900" data-testid="gv-local-banner">
          <WifiOff className="h-4 w-4" />
          <span className="font-semibold">{t("Rapport généré hors ligne sur cet appareil")}</span>
          <span className="text-xs text-amber-800">· {t("il sera envoyé au serveur à la reconnexion ; vous pourrez ensuite le soumettre et télécharger le PDF.")}</span>
        </div>
      )}

      {!isLocal && (staleAt || offline) && (
        <div className="mb-4 flex items-center gap-2 rounded-lg border border-slate-300 bg-slate-100 px-4 py-2.5 text-xs text-slate-700" data-testid="gv-offline-banner">
          <WifiOff className="h-3.5 w-3.5" />
          {staleAt
            ? `${t("Données hors ligne · dernier rafraîchissement le")} ${new Date(staleAt).toLocaleString()}`
            : t("Hors ligne · lecture seule")}
          <span className="text-slate-500">· {t("les actions de validation nécessitent une connexion")}</span>
        </div>
      )}

      {r.status === "submitted" && (
        <div className="mb-4 flex items-center gap-2 rounded-lg border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-900" data-testid="gv-submitted-banner">
          <Clock className="h-4 w-4" /> {t("Soumis pour validation par")} <strong>{r.submitted_by}</strong>
          <span className="text-xs text-amber-700/80">· {t("en attente du superviseur provincial ou de l'Admin")}</span>
        </div>
      )}
      {r.status === "returned" && (
        <div className="mb-4 rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-800" data-testid="gv-returned-banner">
          <p className="flex items-center gap-2 font-semibold"><RotateCcw className="h-4 w-4" /> {t("Renvoyé pour correction par")} {r.returned_by}</p>
          <p className="mt-1 text-xs">{r.returned_reason}</p>
        </div>
      )}
      {r.status === "validated" && (
        <div className="mb-4 flex flex-wrap items-center gap-2 rounded-lg border border-emerald-200 bg-emerald-50 px-4 py-3 text-sm text-emerald-800" data-testid="gv-validated-banner">
          <BadgeCheck className="h-4 w-4" /> {t("Validé et signé par")} <strong>{r.signed_by}</strong>
        </div>
      )}
      {r.status === "consolidated" && (
        <div className="mb-4 flex flex-wrap items-center gap-2 rounded-lg border border-[#0284C7]/30 bg-[#E0F2FE] px-4 py-3 text-sm text-[#075985]" data-testid="gv-consolidated-banner">
          <Stamp className="h-4 w-4" /> {t("Consolidé · double signature")} : <strong>{r.signed_by}</strong>
          {t("puis")} <strong>{r.countersigned_by}</strong>
        </div>
      )}

      <ReportSteps r={r} narr={narr} />

      <section className="bg-white border border-[#E2E8F0] rounded-lg shadow-sm p-5 mb-4" data-testid="gv-indicators">
        <h2 className="text-sm font-semibold text-slate-900 mb-4">{t("Indicateurs agrégés (auto)")}</h2>
        <div className="overflow-x-auto rounded-md border border-slate-200">
          <table className="w-full text-sm">
            <thead><tr className="bg-[#F1F5F9] text-left">
              {["Indicateur", "Période", "Période précédente", "Variation"].map((h) => (
                <th key={h} className="p-2.5 text-xs font-bold text-slate-600">{t(h)}</th>))}
            </tr></thead>
            <tbody>
              {rows.length === 0 ? (
                <tr><td colSpan={4} className="p-4 text-center text-xs text-slate-500">{t("Aucune valeur saisie sur la période.")}</td></tr>
              ) : rows.map((x) => (
                <tr key={x.code} className="border-t border-slate-100">
                  <td className="p-2.5 text-slate-700">{x.label}</td>
                  <td className="p-2.5 font-semibold text-slate-900">{x.current ?? "N/A"}</td>
                  <td className="p-2.5 text-slate-500">{x.previous ?? "N/A"}</td>
                  <td className={`p-2.5 font-semibold ${x.change > 0 ? "text-[#B91C1C]" : x.change < 0 ? "text-emerald-700" : "text-slate-400"}`}>
                    {x.change === null || x.change === undefined ? "-" : `${x.change > 0 ? "+" : ""}${x.change}%`}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </section>

      <ReportDashboard dash={r.dashboard} />

      <section className="bg-white border border-[#E2E8F0] rounded-lg shadow-sm p-5 mb-4" data-testid="gv-reading">
        <div className="flex items-start justify-between gap-3 mb-1">
          <div>
            <h2 className="text-sm font-semibold text-slate-900">{t("Lecture de la situation")}</h2>
            <p className="text-xs text-slate-500">{t("Points saillants, tendances et points d'attention proposés à partir des chiffres. Complétez-les pour expliquer la réalité du terrain : contexte, causes probables, actions engagées.")}</p>
          </div>
          {r.dashboard?.reading?.text && (
            <Button size="sm" variant="outline" className="rounded-md h-7 text-xs shrink-0" onClick={() => setNarr({ ...narr, lecture: r.dashboard.reading.text })} data-testid="gv-reading-reset">
              <RefreshCw className="h-3.5 w-3.5 mr-1" />{t("Reprendre la proposition")}
            </Button>
          )}
        </div>
        <Textarea rows={12} value={narr.lecture || ""} onChange={(e) => setNarr({ ...narr, lecture: e.target.value })} className="mt-3 font-[inherit]" data-testid="gv-narr-lecture" />
      </section>

      <AreasReport areas={r.areas} />
      <ListsReport activities={r.activities} challenges={r.challenges} categories={fwCats} />

      <section className="bg-white border border-[#E2E8F0] rounded-lg shadow-sm p-5 mb-4" data-testid="gv-narratives">
        <h2 className="text-sm font-semibold text-slate-900 mb-1">{t("Analyse narrative")}</h2>
        <p className="text-xs text-slate-500 mb-4">{t("Les notes de terrain de la période sont reportées par thème. Le copilote peut proposer une rédaction, toujours modifiable.")}</p>
        <div className="space-y-6">
          {groups.map((g) => (
            <div key={g.code} data-testid={`gv-theme-${g.code}`}>
              <div className="flex items-center justify-between gap-2 mb-1.5">
                <Label className="text-sm font-semibold text-slate-800">{g.label}</Label>
                <Button size="sm" variant="outline" className="rounded-md h-7 text-xs" onClick={() => copilot(g.code)} disabled={!!busy || offline || isLocal} data-testid={`gv-copilot-${g.code}`}>
                  {busy === `cop-${g.code}` ? <Loader2 className="h-3.5 w-3.5 mr-1 animate-spin" /> : <Wand2 className="h-3.5 w-3.5 mr-1" />}{t("Copilote")}
                </Button>
              </div>
              {g.items.length > 0 && (
                <ul className="mb-2 space-y-1">
                  {g.items.map((it, i) => (
                    <li key={i} className="text-xs text-slate-500 border-l-2 border-[#E2E8F0] pl-2">
                      <span className="font-semibold text-slate-600">{it.date}</span> · {it.text}
                      {it.confidential && (
                        <span className="ml-1.5 inline-flex items-center gap-1 text-[9px] font-semibold uppercase tracking-wider bg-[#0F172A] text-white px-1.5 py-0.5 rounded">
                          <Lock className="h-2.5 w-2.5" />{t("Confidentiel")}
                        </span>
                      )}
                    </li>
                  ))}
                </ul>
              )}
              {g.hidden_confidential > 0 && (
                <p className="mb-2 text-xs italic text-slate-400" data-testid={`gv-hidden-${g.code}`}>
                  <Lock className="h-3 w-3 inline mr-1" />{g.hidden_confidential} {t("note(s) confidentielle(s) masquée(s) pour votre profil.")}
                </p>
              )}
              <Textarea rows={4} value={narr[g.code] || ""} onChange={(e) => setNarr({ ...narr, [g.code]: e.target.value })}
                data-testid={`gv-narr-${g.code}`} />
            </div>
          ))}
        </div>
      </section>

      {(r.photos?.length > 0 || r.hidden_photos > 0) && (
        <section className="bg-white border border-[#E2E8F0] rounded-lg shadow-sm p-5 mb-4" data-testid="gv-photos">
          <h2 className="text-sm font-semibold text-slate-900 mb-4">{t("Photos de terrain")}</h2>
          {r.hidden_photos > 0 ? (
            <p className="text-xs italic text-slate-400" data-testid="gv-photos-hidden">
              <Lock className="h-3 w-3 inline mr-1" />{r.hidden_photos} {t("photo(s) non affichée(s) pour votre profil.")}
            </p>
          ) : (
            <div className="flex flex-wrap gap-3">
              {r.photos.map((p) => (
                <figure key={p.path} className="w-36">
                  <img src={`${process.env.REACT_APP_BACKEND_URL}/api/mr-files/${p.path}?auth=${localStorage.getItem("token")}`}
                    alt="" className="h-28 w-36 rounded-md object-cover border border-slate-200 bg-slate-100" />
                  <figcaption className="mt-1 text-[11px] text-slate-500">{p.date} · {p.author_name}{p.caption ? ` · ${p.caption}` : ""}</figcaption>
                </figure>
              ))}
            </div>
          )}
        </section>
      )}

      {canValidate && !isLocal && <ValidationTrail history={r.history} />}

      <section className="bg-white border border-[#E2E8F0] rounded-lg shadow-sm p-5" data-testid="gv-validate">
        <h2 className="text-sm font-semibold text-slate-900 mb-1">{t("Circuit de validation")}</h2>
        <p className="text-xs text-slate-500 mb-4">{t("L'auteur rédige puis soumet son rapport. Seuls le superviseur provincial et l'Administrateur peuvent le valider et le signer ; l'Administrateur le consolide ensuite.")}</p>

        {canValidate ? (
          <div className="flex flex-col sm:flex-row sm:items-end gap-3">
            <div className="sm:w-72">
              <Label className="text-xs">{t("Nom et prénom du validateur")}</Label>
              <Input value={sig} onChange={(e) => setSig(e.target.value)} data-testid="gv-signature" />
            </div>
            <Button onClick={validate} disabled={!!busy || offline || r.status === "draft"} className="rounded-md bg-[#F05A28] hover:bg-[#D84A1B]" data-testid="gv-validate-btn">
              {busy === "validate" ? <Loader2 className="h-4 w-4 mr-1.5 animate-spin" /> : <CheckCircle2 className="h-4 w-4 mr-1.5" />}
              {t(r.status === "validated" || r.status === "consolidated" ? "Mettre à jour la signature" : "Valider et signer")}
            </Button>
            <Button variant="outline" onClick={sendBack} disabled={!!busy || offline || r.status === "draft"} className="rounded-md text-red-600" data-testid="gv-return-btn">
              {busy === "return" ? <Loader2 className="h-4 w-4 mr-1.5 animate-spin" /> : <RotateCcw className="h-4 w-4 mr-1.5" />}
              {t("Renvoyer pour correction")}
            </Button>
            {isAdmin && r.status === "validated" && (
              <Button onClick={countersign} disabled={!!busy} className="rounded-md bg-[#0284C7] hover:bg-[#0369A1]" data-testid="gv-countersign-btn">
                {busy === "counter" ? <Loader2 className="h-4 w-4 mr-1.5 animate-spin" /> : <Stamp className="h-4 w-4 mr-1.5" />}
                {t("Consolider (Admin)")}
              </Button>
            )}
          </div>
        ) : (
          <div className="flex flex-wrap items-center gap-3">
            <Button onClick={submit} disabled={!!busy || offline || isLocal || ["submitted", "validated", "consolidated"].includes(r.status)}
              className="rounded-md bg-[#F05A28] hover:bg-[#D84A1B]" data-testid="gv-submit-btn">
              {busy === "submit" ? <Loader2 className="h-4 w-4 mr-1.5 animate-spin" /> : <Send className="h-4 w-4 mr-1.5" />}
              {t(r.status === "submitted" ? "Déjà soumis" : "Soumettre pour validation")}
            </Button>
            <p className="text-xs text-slate-500">{t("La validation est réservée au superviseur provincial et à l'Administrateur.")}</p>
          </div>
        )}
      </section>
      {pdf.el}
    </div>
  );
}
